/**
 * PostsCarouselClient Component
 *
 * Diamond Architecture: Client wrapper for PostsCarousel.
 * Handles carousel context so nav arrows can be placed in header.
 * Mirrors ListingCarouselClient pattern exactly.
 */
import { Link } from '@/shared/i18n/routing';
import { useTranslations } from '@/shared/i18n/useTranslations';
import type { Route } from '@letsgethome/routes';
import type { LocalizedText } from '@letsgethome/types/branded';
import { Carousel, CarouselContent, CarouselItem } from '@letsgethome/ui/carousel';
import { CarouselNavGroup } from '@letsgethome/ui/CarouselNavGroup';
import { SectionHeader } from '@letsgethome/ui/SectionHeader';

interface PostsCarouselClientProps {
  title: LocalizedText;
  seeAllHref?: Route;
  children: React.ReactNode;
}

export function PostsCarouselClient({ title, seeAllHref, children }: PostsCarouselClientProps) {
  const t = useTranslations('common');
  return (
    <Carousel
      opts={{
        align: 'start',
        loop: false,
      }}
      roleDescription={t('a11y.carousel')}
      slideRoleDescription={t('a11y.slide')}
      className="w-full"
    >
      <SectionHeader
        title={title}
        href={seeAllHref}
        LinkComponent={Link}
        actions={
          <CarouselNavGroup
            className="hidden md:flex"
            prevLabel={t('a11y.previousSlide')}
            nextLabel={t('a11y.nextSlide')}
          />
        }
      />
      <CarouselContent className="-ml-4">{children}</CarouselContent>
    </Carousel>
  );
}

export { CarouselItem };
